// Development service to simulate Firebase operations
// This only works in development mode with the test user

// Mock user data for development
const DEV_USER_ID = 'dev-user-123';
const DEV_USER = {
  uid: DEV_USER_ID,
  email: 'testapplicant@notarial.online',
  fullName: 'Usuario Applicant de Prueba',
  userType: 'applicant',
  agreeTerms: true,
  applicantDescription: 'Soy un profesional joven buscando alojamiento en Madrid. Trabajo de forma remota como desarrollador.',
  monthlyIncome: 2500,
  withOtherPerson: false,
  otherPersonName: null,
  otherPersonDni: null,
  hasGuarantor: true,
  guarantorInfo: {
    name: 'María García',
    relationship: 'parent',
    status: 'verified'
  },
  // Profile completion fields
  dni: '12345678A',
  dniFrontPictureUrl: 'https://example.com/fake-front-picture.jpg',
  bankNumber: 'ES1234567890123456789012',
  hasCompletedProfile: true
};

// Initialize global storage for mock data
// Using window object to persist data between component mounts/unmounts
if (typeof window !== 'undefined') {
  // Initialize mock contracts if not already present
  window.devMockContracts = window.devMockContracts || {};
  // Initialize mock storage if not already present
  window.devMockStorage = window.devMockStorage || {};
  // Initialize nextContractId if not already present
  window.devNextContractId = window.devNextContractId || 1;
}

// Reference global storage
function getGlobalStorage() {
  return {
    mockContracts: window.devMockContracts || {},
    mockStorage: window.devMockStorage || {},
    nextContractId: window.devNextContractId || 1
  };
}

// Update global storage
function updateGlobalStorage(updates) {
  if (typeof window !== 'undefined') {
    if (updates.mockContracts) window.devMockContracts = updates.mockContracts;
    if (updates.mockStorage) window.devMockStorage = updates.mockStorage;
    if (updates.nextContractId) window.devNextContractId = updates.nextContractId;
  }
}

/**
 * Check if current environment is development and using test credentials
 */
function isDevUser() {
  return (
    (process.env.NODE_ENV === 'development' || process.env.VUE_APP_ENV === 'development') &&
    sessionStorage.getItem('dev_auth_user') !== null
  );
}

/**
 * Get the current development user
 */
function getDevUser() {
  if (!isDevUser()) return null;
  
  const sessionUser = JSON.parse(sessionStorage.getItem('dev_auth_user') || '{}');
  
  // Return DEV_USER merged with session data, ensuring profile completion fields are present
  return { 
    ...DEV_USER, 
    ...sessionUser,
    // Always ensure these fields are present to pass profile completion check
    dni: sessionUser.dni || DEV_USER.dni,
    dniFrontPictureUrl: sessionUser.dniFrontPictureUrl || DEV_USER.dniFrontPictureUrl,
    bankNumber: sessionUser.bankNumber || DEV_USER.bankNumber,
    hasCompletedProfile: true
  };
}

/**
 * Simulate Firestore document fetch for the development user
 */
async function getDevDoc(collection, id) {
  if (collection === 'users' && id === DEV_USER_ID) {
    const sessionUser = sessionStorage.getItem('dev_auth_user') 
      ? JSON.parse(sessionStorage.getItem('dev_auth_user')) 
      : {};
      
    // Always merge with the complete profile fields
    return {
      exists: () => true,
      data: () => ({ 
        ...DEV_USER,
        ...sessionUser,
        // Always ensure these fields are present to pass profile completion check
        dni: sessionUser.dni || DEV_USER.dni,
        dniFrontPictureUrl: sessionUser.dniFrontPictureUrl || DEV_USER.dniFrontPictureUrl,
        bankNumber: sessionUser.bankNumber || DEV_USER.bankNumber,
        hasCompletedProfile: true
      })
    };
  }
  
  const { mockContracts } = getGlobalStorage();
  
  if (collection === 'contracts' && mockContracts[id]) {
    return {
      exists: () => true,
      id: id,
      data: () => ({ ...mockContracts[id] })
    };
  }
  
  return {
    exists: () => false,
    data: () => null
  };
}

/**
 * Simulate adding a document to Firestore
 */
async function addDevDoc(collection, data) {
  const { mockContracts, nextContractId } = getGlobalStorage();
  
  if (collection === 'contracts') {
    const id = `dev-contract-${nextContractId}`;
    
    // Create a new copy of mockContracts with the new contract
    const updatedMockContracts = {
      ...mockContracts,
      [id]: {
        ...data,
        id
      }
    };
    
    // Update global storage
    updateGlobalStorage({
      mockContracts: updatedMockContracts,
      nextContractId: nextContractId + 1
    });
    
    return {
      id
    };
  }
  
  throw new Error('Operation not supported in development mode');
}

/**
 * Simulate updating a document in Firestore
 */
async function updateDevDoc(collection, id, data) {
  const { mockContracts } = getGlobalStorage();
  
  if (collection === 'contracts' && mockContracts[id]) {
    // Create a new copy of mockContracts with the updated contract
    const updatedMockContracts = {
      ...mockContracts,
      [id]: {
        ...mockContracts[id],
        ...data
      }
    };
    
    // Update global storage
    updateGlobalStorage({
      mockContracts: updatedMockContracts
    });
    
    return true;
  }
  
  throw new Error('Document not found or operation not supported');
}

/**
 * Simulate file upload to storage
 */
async function uploadDevFile(file) {
  const { mockStorage } = getGlobalStorage();
  
  const fileId = `dev-file-${Date.now()}-${file.name}`;
  const url = URL.createObjectURL(file);
  
  // Create a new copy of mockStorage with the new file
  const updatedMockStorage = {
    ...mockStorage,
    [fileId]: { url, file }
  };
  
  // Update global storage
  updateGlobalStorage({
    mockStorage: updatedMockStorage
  });
  
  return {
    ref: {
      fullPath: fileId
    }
  };
}

/**
 * Get download URL for simulated file
 */
async function getDevDownloadURL(ref) {
  const { mockStorage } = getGlobalStorage();
  
  const fileId = ref.fullPath;
  return mockStorage[fileId]?.url || '';
}

/**
 * Delete a simulated file
 */
async function deleteDevFile(ref) {
  const { mockStorage } = getGlobalStorage();
  
  const fileId = ref.fullPath;
  if (mockStorage[fileId]) {
    URL.revokeObjectURL(mockStorage[fileId].url);
    
    // Create a new copy of mockStorage without the deleted file
    const updatedMockStorage = { ...mockStorage };
    delete updatedMockStorage[fileId];
    
    // Update global storage
    updateGlobalStorage({
      mockStorage: updatedMockStorage
    });
  }
  
  return true;
}

export {
  isDevUser,
  getDevUser,
  getDevDoc,
  addDevDoc,
  updateDevDoc,
  uploadDevFile,
  getDevDownloadURL,
  deleteDevFile
}; 